<script setup lang="ts">
// Picks what the page shows: a facility, or (for users who can see more than one facility in a tenant) all of them. The
// picker searches every facility the user can see, across tenants, so admins can jump anywhere from it.
import { computed, ref } from 'vue';
import type { FacilityBrief } from '../../cross-app-shared/types/common';
import type { TenantID } from '../../cross-app-shared/types/db';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../command';
import { platformScopeParams, type PlatformScope, type PlatformScopeParams } from './platform-landing';

const props = defineProps<{
  scope: PlatformScope;
  facilities: FacilityBrief[];
}>();

const emit = defineEmits<{
  scopeUpdate: [params: PlatformScopeParams];
}>();

const open = ref(false);

// facilitiesList is sorted by tenant, then group, then name.
const tenants = computed(() => {
  const byTenant = new Map<TenantID, { tenantID: TenantID; name: string; facilities: FacilityBrief[] }>();
  for (const f of props.facilities) {
    let tenant = byTenant.get(f.tenantID);
    if (!tenant) {
      tenant = { tenantID: f.tenantID, name: f.tenantName, facilities: [] };
      byTenant.set(f.tenantID, tenant);
    }
    tenant.facilities.push(f);
  }
  return [...byTenant.values()].sort((a, b) => a.name.localeCompare(b.name));
});

const selectedKey = computed(() =>
  props.scope.kind === 'facility'
    ? `facility-${props.scope.facility.facilityID}`
    : `tenant-${props.scope.tenant.tenantID}`,
);

function facilityChoose(facility: FacilityBrief) {
  open.value = false;
  emit('scopeUpdate', { tenantID: facility.tenantID, facilityID: facility.facilityID, all: false });
}

function tenantChoose(tenantID: TenantID) {
  open.value = false;
  // Keeps the current facility to return to, if it's in that tenant.
  const facilityID = props.scope.facility?.tenantID === tenantID ? props.scope.facility.facilityID : null;
  emit('scopeUpdate', { tenantID, facilityID, all: true });
}

function facilityToggle() {
  if (props.scope.kind === 'facility') return;
  if (props.scope.facility) {
    emit('scopeUpdate', { ...platformScopeParams(props.scope), all: false });
  } else {
    open.value = true;
  }
}

function allToggle() {
  if (props.scope.kind === 'tenant') return;
  emit('scopeUpdate', { ...platformScopeParams(props.scope), all: true });
}
</script>

<template>
  <div class="pl-scope">
    <div v-if="scope.tenantFacilities.length > 1" class="pl-toggle" role="group" aria-label="Facilities shown">
      <button type="button" :aria-pressed="scope.kind === 'facility'" @click="facilityToggle">Facility</button>
      <button type="button" :aria-pressed="scope.kind === 'tenant'" @click="allToggle">All facilities</button>
    </div>

    <Popover v-if="facilities.length > 1" v-model:open="open">
      <PopoverTrigger asChild>
        <button type="button" class="pl-pill pl-pill-picker">
          <span class="pl-pill-text">{{ scope.kind === 'facility' ? scope.facility.name : scope.tenant.name }}</span>
          <i class="fa-solid fa-chevron-down" aria-hidden="true"></i>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" class="w-80 p-0">
        <Command :modelValue="selectedKey">
          <CommandInput placeholder="Search facilities..." :displayValue="() => ''" />
          <CommandEmpty>No facility found.</CommandEmpty>
          <CommandList class="max-h-96">
            <CommandGroup v-for="t in tenants" :key="t.tenantID" :heading="tenants.length > 1 ? t.name : undefined">
              <CommandItem
                v-if="t.facilities.length > 1"
                :value="`tenant-${t.tenantID}`"
                :textValue="`All facilities in ${t.name}`"
                class="cursor-pointer"
                @select="tenantChoose(t.tenantID)"
              >
                <i
                  class="fa-light fa-check mr-2 w-4"
                  :class="{ invisible: selectedKey !== `tenant-${t.tenantID}` }"
                ></i>
                <span class="font-semibold">All facilities in {{ t.name }}</span>
              </CommandItem>
              <CommandItem
                v-for="f in t.facilities"
                :key="f.facilityID"
                :value="`facility-${f.facilityID}`"
                :textValue="`${t.name} ${f.groupName} ${f.name}`"
                class="cursor-pointer"
                @select="facilityChoose(f)"
              >
                <i
                  class="fa-light fa-check mr-2 w-4"
                  :class="{ invisible: selectedKey !== `facility-${f.facilityID}` }"
                ></i>
                <span class="flex min-w-0 flex-col">
                  <span class="truncate">{{ f.name }}</span>
                  <span class="truncate text-xs text-slate-500">{{ f.groupName }}</span>
                </span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    <span v-else class="pl-pill pl-pill-text">{{
      scope.kind === 'facility' ? scope.facility.name : scope.tenant.name
    }}</span>
  </div>
</template>
